import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';
import { resetUploadStatus, selectUploadStatus } from '../../store/slices/eventSlice';
import { FaCheck, FaCloudUploadAlt } from 'react-icons/fa';
import './UploadProgress.css';

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const RING_SIZE = 120;
const RING_STROKE = 6;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const ProgressRing = ({ pct, indeterminate }) => {
  const offset = RING_CIRCUMFERENCE - (pct / 100) * RING_CIRCUMFERENCE;

  return (
    <div className="upload-ring-wrap">
      <svg
        className={`upload-ring ${indeterminate ? 'is-indeterminate' : ''}`}
        width={RING_SIZE}
        height={RING_SIZE}
        viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
        aria-hidden="true"
      >
        <circle
          className="upload-ring-track"
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          strokeWidth={RING_STROKE}
        />
        <circle
          className={`upload-ring-fill ${indeterminate ? 'is-indeterminate' : ''}`}
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          strokeWidth={RING_STROKE}
          strokeDasharray={RING_CIRCUMFERENCE}
          strokeDashoffset={indeterminate ? RING_CIRCUMFERENCE * 0.75 : offset}
        />
      </svg>
      <div className="upload-ring-center">
        {indeterminate ? (
          <FaCloudUploadAlt className="upload-ring-icon" />
        ) : (
          <span className="upload-ring-pct">{pct}%</span>
        )}
      </div>
      <span className="upload-ring-pulse" aria-hidden="true" />
      <span className="upload-ring-pulse upload-ring-pulse--delayed" aria-hidden="true" />
    </div>
  );
};

const UploadProgress = () => {
  const dispatch = useDispatch();
  const uploadStatus = useSelector(selectUploadStatus);
  const [showSuccess, setShowSuccess] = useState(false);

  const progress = uploadStatus.progress || {};
  const hasReal = progress.total > 0;
  const pct = hasReal
    ? Math.min(100, Math.round((progress.loaded / progress.total) * 100))
    : 0;

  const filesTotal = progress.filesTotal || 0;
  const filesCompleted = progress.filesCompleted || 0;
  const filesRemaining = Math.max(0, filesTotal - filesCompleted);

  useEffect(() => {
    if (!uploadStatus.loading && uploadStatus.success) {
      setShowSuccess(true);

      const timeoutId = setTimeout(() => {
        setShowSuccess(false);
        dispatch(resetUploadStatus());
      }, 2800);

      return () => clearTimeout(timeoutId);
    }
  }, [dispatch, uploadStatus.loading, uploadStatus.success]);

  return (
    <>
      <AnimatePresence>
        {uploadStatus.loading && (
          <motion.div
            key="upload-overlay"
            className="upload-progress-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <motion.div
              className="upload-progress-container"
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 380, damping: 28 }}
            >
              <ProgressRing pct={pct} indeterminate={!hasReal} />

              <div className="upload-progress-text">
                <h3>{hasReal ? 'Uploading your media' : 'Preparing upload…'}</h3>
                <p>
                  {filesTotal > 0
                    ? `${filesCompleted} of ${filesTotal} file${filesTotal === 1 ? '' : 's'} done`
                    : 'Please wait while your files are being uploaded'}
                </p>
              </div>

              {filesTotal > 0 && (
                <div className="upload-file-dots" aria-hidden="true">
                  {Array.from({ length: Math.min(filesTotal, 12) }).map((_, i) => (
                    <span
                      key={i}
                      className={`upload-file-dot ${i < filesCompleted ? 'is-done' : i === filesCompleted ? 'is-active' : ''}`}
                    />
                  ))}
                  {filesTotal > 12 && (
                    <span className="upload-file-dot-more">+{filesTotal - 12}</span>
                  )}
                </div>
              )}

              <div
                className={`upload-progress-bar ${hasReal ? 'is-determinate' : ''}`}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={hasReal ? pct : undefined}
                aria-label="Upload progress"
              >
                <div
                  className="upload-progress-fill"
                  style={hasReal ? { width: `${pct}%` } : undefined}
                >
                  <span className="upload-progress-shimmer" />
                </div>
              </div>

              <div className="upload-progress-meta">
                {hasReal && (
                  <span className="upload-meta-chip">
                    {formatBytes(progress.loaded)} / {formatBytes(progress.total)}
                  </span>
                )}
                {filesRemaining > 0 && filesTotal > 0 && (
                  <span className="upload-meta-chip upload-meta-chip--muted">
                    {filesRemaining} remaining
                  </span>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSuccess && (
          <motion.div
            key="upload-success"
            className="upload-success-toast"
            initial={{ opacity: 0, y: -20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 420, damping: 26 }}
          >
            <div className="upload-success-badge">
              <FaCheck className="upload-success-check" />
            </div>
            <div className="upload-success-text">
              <strong>Upload complete</strong>
              <span>Your pictures were uploaded successfully.</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default UploadProgress;
